// Premium Luxury Color System for Unschooling Website
// Sophisticated, professional colors for a luxury brand experience

export const colorSystem = {
  // Primary Brand Colors - Deep Navy & Charcoal
  primary: {
    50: '#f8fafc',   // Very light gray-blue
    100: '#f1f5f9',  // Light gray-blue
    200: '#e2e8f0',  // Lighter gray-blue
    300: '#cbd5e1',  // Light gray-blue
    400: '#94a3b8',  // Medium gray-blue
    500: '#64748b',  // Base slate
    600: '#475569',  // Darker slate
    700: '#334155',  // Dark slate
    800: '#1e293b',  // Darker slate
    900: '#0f172a',  // Very dark slate
  },

  // Secondary Accent Colors - Rich Gold & Bronze
  secondary: {
    50: '#fefdf8',   // Very light cream
    100: '#fdf7e8',  // Light cream
    200: '#f9e7b5',  // Lighter warm beige
    300: '#f4d68a',  // Light warm beige
    400: '#e8c55c',  // Medium warm gold
    500: '#d4a574',  // Base warm gold
    600: '#b8945f',  // Darker warm gold
    700: '#9c7c4a',  // Dark warm gold
    800: '#80643a',  // Darker warm gold
    900: '#654c2e',  // Very dark warm gold
  },

  // Luxury Accent Colors - Premium Palette
  luxury: {
    // Deep Emerald
    emerald: {
      50: '#ecfdf5',   // Very light emerald
      100: '#d1fae5',  // Light emerald
      200: '#a7f3d0',  // Lighter emerald
      300: '#6ee7b7',  // Light emerald
      400: '#34d399',  // Medium emerald
      500: '#10b981',  // Base emerald
      600: '#059669',  // Darker emerald
      700: '#047857',  // Dark emerald
      800: '#065f46',  // Darker emerald
      900: '#064e3b',  // Very dark emerald
    },
    // Royal Purple
    purple: {
      50: '#faf5ff',   // Very light purple
      100: '#f3e8ff',  // Light purple
      200: '#e9d5ff',  // Lighter purple
      300: '#d8b4fe',  // Light purple
      400: '#c084fc',  // Medium purple
      500: '#a855f7',  // Base purple
      600: '#9333ea',  // Darker purple
      700: '#7c3aed',  // Dark purple
      800: '#6b21a8',  // Darker purple
      900: '#581c87',  // Very dark purple
    },
    // Sapphire Blue
    sapphire: {
      50: '#eff6ff',   // Very light sapphire
      100: '#dbeafe',  // Light sapphire
      200: '#bfdbfe',  // Lighter sapphire
      300: '#93c5fd',  // Light sapphire
      400: '#60a5fa',  // Medium sapphire
      500: '#3b82f6',  // Base sapphire
      600: '#2563eb',  // Darker sapphire
      700: '#1d4ed8',  // Dark sapphire
      800: '#1e40af',  // Darker sapphire
      900: '#1e3a8a',  // Very dark sapphire
    },
    // Rose Gold
    rose: {
      50: '#fff1f2',   // Very light rose
      100: '#ffe4e6',  // Light rose
      200: '#fecdd3',  // Lighter rose
      300: '#fda4af',  // Light rose
      400: '#fb7185',  // Medium rose
      500: '#f43f5e',  // Base rose
      600: '#e11d48',  // Darker rose
      700: '#be123c',  // Dark rose
      800: '#9f1239',  // Darker rose
      900: '#881337',  // Very dark rose
    },
    // Amber Gold
    amber: {
      50: '#fffbeb',   // Very light amber
      100: '#fef3c7',  // Light amber
      200: '#fde68a',  // Lighter amber
      300: '#fcd34d',  // Light amber
      400: '#fbbf24',  // Medium amber
      500: '#f59e0b',  // Base amber
      600: '#d97706',  // Darker amber
      700: '#b45309',  // Dark amber
      800: '#92400e',  // Darker amber
      900: '#78350f',  // Very dark amber
    },
    // Teal
    teal: {
      50: '#f0fdfa',   // Very light teal
      100: '#ccfbf1',  // Light teal
      200: '#99f6e4',  // Lighter teal
      300: '#5eead4',  // Light teal
      400: '#2dd4bf',  // Medium teal
      500: '#14b8a6',  // Base teal
      600: '#0d9488',  // Darker teal
      700: '#0f766e',  // Dark teal
      800: '#115e59',  // Darker teal
      900: '#134e4a',  // Very dark teal
    },
    // Indigo
    indigo: {
      50: '#eef2ff',   // Very light indigo
      100: '#e0e7ff',  // Light indigo
      200: '#c7d2fe',  // Lighter indigo
      300: '#a5b4fc',  // Light indigo
      400: '#818cf8',  // Medium indigo
      500: '#6366f1',  // Base indigo
      600: '#4f46e5',  // Darker indigo
      700: '#4338ca',  // Dark indigo
      800: '#3730a3',  // Darker indigo
      900: '#312e81',  // Very dark indigo
    },
    // Coral
    coral: {
      50: '#fff7ed',   // Very light coral
      100: '#ffedd5',  // Light coral
      200: '#fed7aa',  // Lighter coral
      300: '#fdba74',  // Light coral
      400: '#fb923c',  // Medium coral
      500: '#f97316',  // Base coral
      600: '#ea580c',  // Darker coral
      700: '#c2410c',  // Dark coral
      800: '#9a3412',  // Darker coral
      900: '#7c2d12',  // Very dark coral
    },
  },

  // Neutral Grays
  neutral: {
    50: '#f8fafc',   // Very light gray
    100: '#f1f5f9',  // Light gray
    200: '#e2e8f0',  // Lighter gray
    300: '#cbd5e1',  // Light gray
    400: '#94a3b8',  // Medium gray
    500: '#64748b',  // Base gray
    600: '#475569',  // Darker gray
    700: '#334155',  // Dark gray
    800: '#1e293b',  // Darker gray
    900: '#0f172a',  // Very dark gray
  },

  // Semantic Colors - Sophisticated & Muted
  semantic: {
    success: {
      50: '#f0fdf4',
      100: '#dcfce7',
      200: '#bbf7d0',
      300: '#86efac',
      400: '#4ade80',
      500: '#22c55e',
      600: '#16a34a',
      700: '#15803d',
      800: '#166534',
      900: '#14532d',
    },
    warning: {
      50: '#fefdf8',   // Very light cream
      100: '#fdf7e8',  // Light cream
      200: '#f9e7b5',  // Warm beige
      300: '#f4d68a',  // Light warm gold
      400: '#e8c55c',  // Medium warm gold
      500: '#d4a574',  // Base warm gold
      600: '#b8945f',  // Darker warm gold
      700: '#9c7c4a',  // Dark warm gold
      800: '#80643a',  // Darker warm gold
      900: '#654c2e',  // Very dark warm gold
    },
    error: {
      50: '#fef7f7',   // Very light warm gray
      100: '#fce8e8',  // Light warm gray
      200: '#f7c7c7',  // Lighter warm red
      300: '#f0a0a0',  // Light warm red
      400: '#e67e7e',  // Medium warm red
      500: '#d97777',  // Base warm red
      600: '#c55a5a',  // Darker warm red
      700: '#a04747',  // Dark warm red
      800: '#7a3535',  // Darker warm red
      900: '#542424',  // Very dark warm red
    },
    info: {
      50: '#f8fafc',   // Very light gray-blue
      100: '#f1f5f9',  // Light gray-blue
      200: '#e2e8f0',  // Lighter gray-blue
      300: '#cbd5e1',  // Light gray-blue
      400: '#94a3b8',  // Medium gray-blue
      500: '#64748b',  // Base slate
      600: '#475569',  // Darker slate
      700: '#334155',  // Dark slate
      800: '#1e293b',  // Darker slate
      900: '#0f172a',  // Very dark slate
    },
  },

  // Background Colors - Luxury & Sophisticated
  background: {
    primary: '#ffffff',      // Pure white
    secondary: '#fafbfc',    // Very light warm gray
    tertiary: '#f4f6f8',     // Light warm gray
    muted: '#f8f9fa',        // Very light warm gray
    accent: '#f9fafb',       // Very light warm gray
  },

  // Text Colors - Sophisticated Hierarchy
  text: {
    primary: '#1a202c',      // Very dark warm gray
    secondary: '#2d3748',    // Dark warm gray
    tertiary: '#4a5568',     // Medium warm gray
    muted: '#718096',        // Light warm gray
    inverse: '#ffffff',      // White text
    accent: '#64748b',       // Sophisticated slate
  },

  // Border Colors - Subtle & Sophisticated
  border: {
    light: '#e2e8f0',        // Light warm gray
    medium: '#d1d5db',       // Medium warm gray
    dark: '#9ca3af',         // Darker warm gray
    accent: '#64748b',       // Sophisticated slate
  },

  // Shadow Colors - Subtle & Elegant
  shadow: {
    light: 'rgba(0, 0, 0, 0.02)',
    medium: 'rgba(0, 0, 0, 0.04)',
    dark: 'rgba(0, 0, 0, 0.08)',
    colored: 'rgba(100, 116, 139, 0.1)', // Sophisticated slate with opacity
  },

  // Gradient Definitions - Premium Luxury
  gradients: {
    primary: 'linear-gradient(135deg, #64748b 0%, #475569 100%)',
    secondary: 'linear-gradient(135deg, #d4a574 0%, #b8945f 100%)',
    neutral: 'linear-gradient(135deg, #fafbfc 0%, #f4f6f8 100%)',
    success: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    warning: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
    error: 'linear-gradient(135deg, #f43f5e 0%, #e11d48 100%)',
    info: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    // Luxury gradients
    emerald: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    purple: 'linear-gradient(135deg, #a855f7 0%, #9333ea 100%)',
    sapphire: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    rose: 'linear-gradient(135deg, #f43f5e 0%, #e11d48 100%)',
    amber: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
    teal: 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)',
    indigo: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
    coral: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
  },

  // Component-Specific Colors - Luxury & Sophisticated
  components: {
    button: {
      primary: {
        background: '#64748b',
        text: '#ffffff',
        hover: '#475569',
        active: '#334155',
      },
      secondary: {
        background: '#f4f6f8',
        text: '#2d3748',
        hover: '#e2e8f0',
        active: '#cbd5e1',
      },
      success: {
        background: '#22c55e',
        text: '#ffffff',
        hover: '#16a34a',
        active: '#15803d',
      },
    },
    card: {
      background: '#ffffff',
      border: '#e2e8f0',
      shadow: 'rgba(0, 0, 0, 0.02)',
      hover: 'rgba(0, 0, 0, 0.04)',
    },
    input: {
      background: '#ffffff',
      border: '#d1d5db',
      focus: '#64748b',
      placeholder: '#9ca3af',
    },
  },
};

// Helper function to get color with opacity
export const withOpacity = (color, opacity) => {
  // Convert hex to rgb if needed
  if (color.startsWith('#')) {
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }
  return color;
};

// Helper function to get semantic color
export const getSemanticColor = (type, variant = 500) => {
  return colorSystem.semantic[type]?.[variant] || colorSystem.neutral[variant];
};

// Helper function to get component color
export const getComponentColor = (component, variant = 'primary') => {
  return colorSystem.components[component]?.[variant] || colorSystem.neutral[500];
};

// Helper function to apply color scheme to components
export const applyColorScheme = (scheme = 'default') => {
  const schemes = {
    default: {
      primary: colorSystem.primary,
      secondary: colorSystem.secondary,
      neutral: colorSystem.neutral,
      background: colorSystem.background,
      text: colorSystem.text,
    },
    dark: {
      primary: colorSystem.primary,
      secondary: colorSystem.secondary,
      neutral: colorSystem.neutral,
      background: {
        primary: '#0f172a',
        secondary: '#1e293b',
        tertiary: '#334155',
        muted: '#475569',
        accent: '#1e3a8a',
      },
      text: {
        primary: '#ffffff',
        secondary: '#e2e8f0',
        tertiary: '#cbd5e1',
        muted: '#94a3b8',
        inverse: '#0f172a',
        accent: '#38bdf8',
      },
    },
  };
  
  return schemes[scheme] || schemes.default;
};

export default colorSystem;
